import { useRef } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { fileToDataUrl } from "@/lib/site";
import { MentorAvatar } from "@/components/MentorCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const EMPTY_MENTOR = {
  name: "", email: "", headline: "", bio: "", photo: "", expertise: "", linkedin: "", experience_years: "",
  languages: "", session_price: "", monthly_price: "", session_minutes: 45, monthly_includes: "",
};

export const toMentorPayload = (f) => ({
  ...f,
  expertise: String(f.expertise || "").split(",").map((t) => t.trim()).filter(Boolean),
  experience_years: Number(f.experience_years) || 0,
  session_price: Number(f.session_price) || 0,
  monthly_price: Number(f.monthly_price) || 0,
  session_minutes: Number(f.session_minutes) || 45,
});

export const fromMentor = (m) => ({ ...EMPTY_MENTOR, ...m, expertise: (m.expertise || []).join(", ") });

const Field = ({ label, hint, children }) => (
  <div><Label>{label} {hint && <span className="text-slate-400 font-normal text-xs">{hint}</span>}</Label><div className="mt-1.5">{children}</div></div>
);

export const MentorForm = ({ form, setForm, currency = "INR", idPrefix = "mentor" }) => {
  const fileRef = useRef();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { setForm((s) => ({ ...s, photo: "" })); const url = await fileToDataUrl(f); setForm((s) => ({ ...s, photo: url })); }
    catch (err) { toast.error(err.message); }
    e.target.value = "";
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <MentorAvatar mentor={{ name: form.name || "?", photo: form.photo }} className="w-20 h-20 text-2xl" />
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${idPrefix}-photo-input`} />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-lg" data-testid={`${idPrefix}-photo-btn`}><Upload className="w-4 h-4 mr-1" /> Photo</Button>
            {form.photo && <Button type="button" variant="ghost" size="sm" onClick={() => setForm((s) => ({ ...s, photo: "" }))} data-testid={`${idPrefix}-photo-remove`}><Trash2 className="w-4 h-4" /></Button>}
          </div>
          <p className="font-body text-xs text-slate-400 mt-1">Square image, under 500KB</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name"><Input value={form.name} onChange={set("name")} className="h-11 rounded-xl" data-testid={`${idPrefix}-name`} /></Field>
        <Field label="Email" hint="(kept private)"><Input type="email" value={form.email} onChange={set("email")} className="h-11 rounded-xl" data-testid={`${idPrefix}-email`} /></Field>
      </div>
      <Field label="Headline" hint="e.g. Product leader · ex-Flipkart · career switch coach"><Input value={form.headline} onChange={set("headline")} className="h-11 rounded-xl" data-testid={`${idPrefix}-headline`} /></Field>
      <Field label="About you" hint="what you help with, who you mentor best"><Textarea value={form.bio} onChange={set("bio")} rows={5} className="rounded-xl" data-testid={`${idPrefix}-bio`} /></Field>
      <Field label="Expertise tags" hint="comma separated"><Input value={form.expertise} onChange={set("expertise")} placeholder="Product management, Career switch, Interview prep" className="h-11 rounded-xl" data-testid={`${idPrefix}-expertise`} /></Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Years of experience"><Input type="number" min="0" value={form.experience_years} onChange={set("experience_years")} className="h-11 rounded-xl" data-testid={`${idPrefix}-experience`} /></Field>
        <Field label="Languages"><Input value={form.languages} onChange={set("languages")} placeholder="English, Hindi" className="h-11 rounded-xl" data-testid={`${idPrefix}-languages`} /></Field>
        <Field label="LinkedIn URL"><Input value={form.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/in/…" className="h-11 rounded-xl" data-testid={`${idPrefix}-linkedin`} /></Field>
      </div>
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 grid sm:grid-cols-3 gap-4">
        <Field label={`Session price (${currency})`} hint="0 = not offered"><Input type="number" min="0" value={form.session_price} onChange={set("session_price")} className="h-11 rounded-xl bg-white" data-testid={`${idPrefix}-session-price`} /></Field>
        <Field label="Session length (min)"><Input type="number" min="15" value={form.session_minutes} onChange={set("session_minutes")} className="h-11 rounded-xl bg-white" data-testid={`${idPrefix}-session-minutes`} /></Field>
        <Field label={`Monthly plan (${currency})`} hint="0 = not offered"><Input type="number" min="0" value={form.monthly_price} onChange={set("monthly_price")} className="h-11 rounded-xl bg-white" data-testid={`${idPrefix}-monthly-price`} /></Field>
        <div className="sm:col-span-3"><Field label="What the monthly plan includes" hint="one point per line"><Textarea value={form.monthly_includes} onChange={set("monthly_includes")} rows={3} placeholder={"4 sessions a month\nWhatsApp support between sessions\nResume & LinkedIn review"} className="rounded-xl bg-white" data-testid={`${idPrefix}-monthly-includes`} /></Field></div>
      </div>
    </div>
  );
};
